'use client';

import { useSyncExternalStore } from 'react';
import { useTranslations } from 'app/hooks/useTranslations';
import { cn } from 'app/lib/utils';
import { AlertTriangle, CheckCircle2, Info, OctagonAlert, X } from 'lucide-react';

import {
  dismissToast,
  getServerToastsSnapshot,
  getToastsSnapshot,
  pauseToast,
  resumeToast,
  subscribeToasts,
  ToastItem,
  ToastVariant,
} from './toastStore';

const VARIANT_STYLES: Record<ToastVariant, string> = {
  success: 'border-green-700 bg-green-50 text-green-900',
  info: 'border-blue-700 bg-blue-50 text-blue-900',
  warning: 'border-amber-700 bg-amber-50 text-amber-900',
  error: 'border-red-700 bg-red-50 text-red-900',
};

// DESIGN.md toast: black, white body text, 8px radius, level-2 shadow; the
// icon alone carries the variant colour (state-finished / state-ongoing /
// danger).
const DS_TOAST =
  'ds-scope rounded-ds-sm bg-ds-ink text-ds-on-ink font-ds-ui shadow-[0_8px_30px_rgba(0,0,0,0.08)]';

const DS_ICON_COLOR: Record<ToastVariant, string> = {
  success: 'text-ds-state-finished',
  info: 'text-ds-state-open',
  warning: 'text-ds-state-ongoing',
  error: 'text-ds-danger',
};

const VARIANT_ICONS: Record<ToastVariant, typeof CheckCircle2> = {
  success: CheckCircle2,
  info: Info,
  warning: AlertTriangle,
  error: OctagonAlert,
};

function ToastCard({ item }: { item: ToastItem }) {
  const { t } = useTranslations();
  const Icon = VARIANT_ICONS[item.variant];
  return (
    <div
      role={item.variant === 'error' ? 'alert' : 'status'}
      onMouseEnter={() => pauseToast(item.id)}
      onMouseLeave={() => resumeToast(item.id)}
      onFocus={() => pauseToast(item.id)}
      onBlur={() => resumeToast(item.id)}
      className={cn(
        'pointer-events-auto flex items-start gap-3 px-4 py-3 text-sm',
        item.ds
          ? DS_TOAST
          : ['rounded-md border-l-4 shadow-lg', VARIANT_STYLES[item.variant]]
      )}
    >
      <Icon
        className={cn(
          'mt-0.5 h-5 w-5 shrink-0',
          item.ds && DS_ICON_COLOR[item.variant]
        )}
        aria-hidden="true"
      />
      <p className={cn('flex-1', item.ds ? 'font-normal' : 'font-medium')}>
        {item.message}
      </p>
      <button
        type="button"
        onClick={() => dismissToast(item.id)}
        aria-label={t('close')}
        className="-m-2 shrink-0 rounded p-3 focus:outline-none focus:ring-2 focus:ring-current"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}

export function Toaster() {
  const toasts = useSyncExternalStore(
    subscribeToasts,
    getToastsSnapshot,
    getServerToastsSnapshot
  );

  return (
    <div
      className="pointer-events-none fixed bottom-4 right-4 z-[100] flex w-96 max-w-[calc(100vw-2rem)] flex-col gap-2"
    >
      {toasts.map(item => (
        <ToastCard key={item.id} item={item} />
      ))}
    </div>
  );
}
